import React, { useEffect, useState, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';
import './App.css';

// Schema.org
import SchemaOrg from './components/common/SchemaOrg';

// Analytics
import { initGA, pageview } from './utils/analytics';
import { initTagManager } from './utils/tagManager';
import useAnalytics from './hooks/useAnalytics';

// Components
import Layout from './components/layout/Layout';
import ScrollDepthTracker from './components/common/ScrollDepthTracker';
import CookieConsent from './components/common/CookieConsent';

// Pages
import Home from './pages/Home';
const Services = React.lazy(() => import('./pages/Services'));
const Vitrine = React.lazy(() => import('./pages/Services/Vitrine'));
const Refonte = React.lazy(() => import('./pages/Services/Refonte'));
const SEOPage = React.lazy(() => import('./pages/Services/SEO'));
const Maintenance = React.lazy(() => import('./pages/Services/Maintenance'));
const Portfolio = React.lazy(() => import('./pages/Portfolio'));
const About = React.lazy(() => import('./pages/About'));
const Audit = React.lazy(() => import('./pages/Audit'));
const Contact = React.lazy(() => import('./pages/Contact'));
const Privacy = React.lazy(() => import('./pages/Privacy'));
const Terms = React.lazy(() => import('./pages/Terms'));
const Blog = React.lazy(() => import('./pages/Blog'));
const Article = React.lazy(() => import('./pages/Blog/Article'));
const FAQ = React.lazy(() => import('./pages/FAQ'));
const Meyreuil = React.lazy(() => import('./pages/Local/Meyreuil'));
const Aix = React.lazy(() => import('./pages/Local/Aix'));
const ProgrammaticPage = React.lazy(() => import('./pages/Programmatic'));

// Hubs SEO Programmatique
const ServiceHub = React.lazy(() => import('./pages/Programmatic/Hubs/ServiceHub'));
const MetierHub = React.lazy(() => import('./pages/Programmatic/Hubs/MetierHub'));
const VilleHub = React.lazy(() => import('./pages/Programmatic/Hubs/VilleHub'));
const AllMetiers = React.lazy(() => import('./pages/Programmatic/Hubs/AllMetiers'));
const AllVilles = React.lazy(() => import('./pages/Programmatic/Hubs/AllVilles'));

// Scroll to top on route change and track page views
function ScrollToTop() {
  const { pathname } = useLocation();
  const { trackPageView } = useAnalytics();
  
  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0);
    
    // Track page view
    if (process.env.NODE_ENV === 'production') {
      pageview(pathname);
      if (typeof trackPageView === 'function') {
        trackPageView(pathname);
      }
    }
  }, [pathname, trackPageView]);
  
  return null;
}

function App() {
  const [trackingEnabled, setTrackingEnabled] = useState(() => {
    // Vérifier le consentement stocké
    const consent = localStorage.getItem('cookieConsent');
    return consent === 'accepted' || consent === null; // Par défaut, on active le suivi
  });
  
  // Utiliser les hooks de suivi analytique avec des options avancées
  useAnalytics({
    enableExitTracking: trackingEnabled,
    enableScrollTracking: trackingEnabled,
    enableElementTracking: trackingEnabled,
    exitTrackingOptions: {
      inactivityTimeout: 60000, // 60 secondes d'inactivité
      trackBrowserClose: true
    },
    scrollTrackingOptions: {
      thresholds: [10, 25, 50, 75, 90, 100],
      trackSections: true
    }
  });
  
  // Gérer l'acceptation des cookies
  const handleAcceptCookies = () => {
    setTrackingEnabled(true);
    // Initialiser les outils de suivi
    if (process.env.NODE_ENV === 'production') {
      initTagManager('GTM-5KB4WPCN');
      if (process.env.REACT_APP_GA_TRACKING_ID) {
        initGA(process.env.REACT_APP_GA_TRACKING_ID);
      }
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('consent', 'update', {
          ad_storage: 'granted',
          analytics_storage: 'granted'
        });
      }
    }
  };
  
  // Gérer le refus des cookies
  const handleDeclineCookies = () => {
    setTrackingEnabled(false);
    // Désactiver les outils de suivi
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'consent_update',
        'consent_status': 'declined'
      });
    }
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        ad_storage: 'denied',
        analytics_storage: 'denied'
      });
    }
  };
  
  // Initialize Google Tag Manager, Google Analytics and Google Ads
  useEffect(() => {
    // Vérifier si l'utilisateur a déjà accepté les cookies
    const consent = localStorage.getItem('cookieConsent');
    
    if (consent === 'accepted' && process.env.NODE_ENV === 'production') {
      // Initialize Google Tag Manager with ID
      initTagManager('GTM-5KB4WPCN');
      
      // Initialize Google Analytics if tracking ID exists
      if (process.env.REACT_APP_GA_TRACKING_ID) {
        initGA(process.env.REACT_APP_GA_TRACKING_ID);
      }
      
      // Initialize Google Ads with conversion ID
      import('./utils/analytics').then(({ initGoogleAds }) => {
        initGoogleAds('AW-17504442391');
      });
    }
  }, []);
  
  return (
    <Router>
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#2563EB" />
        <link rel="icon" href="/logo.png" />
        <title>Nexlyx | Développement Web sur Mesure</title>
        <meta name="description" content="Nexlyx - Agence de développement web à Meyreuil. Création de sites vitrines, refonte de sites existants, optimisation SEO et maintenance web." />
        <meta name="keywords" content="développement web, création site internet, site vitrine, refonte site web, SEO, Meyreuil, PACA" />
        <meta name="author" content="Nexlyx" />
        <meta name="robots" content="index, follow" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://nexlyx.fr" />
        <meta property="og:title" content="Nexlyx | Développement Web sur Mesure" />
        <meta property="og:description" content="Agence de développement web à Meyreuil. Création de sites vitrines, refonte de sites existants, optimisation SEO et maintenance web." />
        <meta property="og:image" content="https://nexlyx.fr/logo.png" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://nexlyx.fr" />
        <meta property="twitter:title" content="Nexlyx | Développement Web sur Mesure" />
        <meta property="twitter:description" content="Agence de développement web à Meyreuil. Création de sites vitrines, refonte de sites existants, optimisation SEO et maintenance web." />
        <meta property="twitter:image" content="https://nexlyx.fr/logo.png" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://nexlyx.fr" />
        <link rel="alternate" hrefLang="fr-FR" href="https://nexlyx.fr" />
        {process.env.REACT_APP_GSC_VERIFICATION && (
          <meta name="google-site-verification" content={process.env.REACT_APP_GSC_VERIFICATION} />
        )}
      </Helmet>
      
      <SchemaOrg 
        url={process.env.REACT_APP_SITE_URL || 'https://nexlyx.fr'}
        title={`${process.env.REACT_APP_SITE_NAME || 'Nexlyx'} | Développement Web sur Mesure`}
        description={process.env.REACT_APP_SITE_DESCRIPTION || 'Agence de développement web à Meyreuil. Création de sites vitrines, refonte de sites existants, optimisation SEO et maintenance web.'}
        image={`${process.env.REACT_APP_SITE_URL || 'https://nexlyx.fr'}/logo.png`}
        organization={{
          name: process.env.REACT_APP_SITE_NAME || 'Nexlyx',
          logo: `${process.env.REACT_APP_SITE_URL || 'https://nexlyx.fr'}/logo.png`,
        }}
      />
      
      <ScrollToTop />
      
      <Layout>
        <ScrollDepthTracker />
        <AnimatePresence mode="wait">
          <Suspense fallback={<div className="p-10 text-center text-gray-500">Chargement…</div>}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/site-vitrine" element={<Vitrine />} />
              <Route path="/services/refonte" element={<Refonte />} />
              <Route path="/services/seo" element={<SEOPage />} />
              <Route path="/services/maintenance" element={<Maintenance />} />
              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/about" element={<About />} />
              <Route path="/audit" element={<Audit />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<Article />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/meyreuil" element={<Meyreuil />} />
              <Route path="/aix-en-provence" element={<Aix />} />
              
              {/* Hubs SEO Programmatique */}
              <Route path="/services/:serviceSlug" element={<ServiceHub />} />
              <Route path="/metiers" element={<AllMetiers />} />
              <Route path="/metiers/:metierSlug" element={<MetierHub />} />
              <Route path="/villes" element={<AllVilles />} />
              <Route path="/villes/:villeSlug" element={<VilleHub />} />
              
              {/* Route SEO Programmatique (3 segments) */}
              <Route path="/:serviceSlug/:metierSlug/:villeSlug" element={<ProgrammaticPage />} />
            </Routes>
          </Suspense>
        </AnimatePresence>
      </Layout>
      
      {/* Popup de consentement aux cookies */}
      <CookieConsent 
        onAccept={handleAcceptCookies}
        onDecline={handleDeclineCookies}
      />
    </Router>
  );
}

export default App;
